import React from "react";
import {
  Box,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import moment from "moment";
import DetalleOrdenPagoDocumento from "./DetalleOrdenPagoDocumento";
import {
  etiquetasMediosWizard,
  formatCuit,
  formatMoneda,
  etiquetaEstadoOp,
  lineasDesgloseMedios,
  obtenerDetalleOp,
  obtenerResumenPreview,
  requiereMediosPago,
} from "./utils";

const VistaOrdenPagoDocumento = ({
  preview,
  detalle: detalleProp,
  comprobantesTabla,
  medioPago,
  cuentasOrigenDetalle = [],
  nroOp,
  estadoOrden,
  estadoLabel,
}) => {
  const proveedor = preview?.proveedor ?? {};
  const detalle = detalleProp ?? obtenerDetalleOp(preview);
  const lineasMedios = lineasDesgloseMedios(medioPago, cuentasOrigenDetalle);
  const totalNeto = obtenerResumenPreview(preview)?.total_a_pagar;
  const sinImporteNeto = totalNeto != null && !requiereMediosPago(totalNeto);
  const labelEstado = etiquetaEstadoOp(estadoOrden, estadoLabel);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        mb: 2,
        backgroundColor: "#fff",
        border: "1px solid #d0dce8",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Orden de Pago a Proveedores
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Documento principal — valores enviados y comprobantes cancelados
          </Typography>
          {nroOp ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              N° OP: <strong>{nroOp}</strong>
              {estadoOrden || estadoLabel ? (
                <>
                  {" "}
                  · Estado:{" "}
                  <Box component="span">
                    {labelEstado}
                  </Box>
                </>
              ) : null}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 2,
          p: 1.5,
          backgroundColor: "#f8fafc",
          borderRadius: 1,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Proveedor
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {proveedor.razon_social ?? "—"}
          </Typography>
          <Typography variant="body2">
            CUIT: {formatCuit(proveedor.nro_cuit)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Medios de pago
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {sinImporteNeto
              ? "Sin transferencia · ajuste"
              : etiquetasMediosWizard(medioPago)}
          </Typography>
          <Typography variant="body2">
            Fecha de pago:{" "}
            {moment.isMoment(medioPago?.fechaPago)
              ? medioPago.fechaPago.format("DD/MM/YYYY")
              : medioPago?.fechaPago
                ? moment(medioPago.fechaPago).format("DD/MM/YYYY")
                : "—"}
          </Typography>
          {lineasMedios.map((linea) => (
            <Typography
              key={linea.key}
              variant="body2"
              color={linea.secundaria ? "text.secondary" : "text.primary"}
              sx={linea.secundaria ? { pl: 1.5 } : undefined}
            >
              {linea.label}: {formatMoneda(linea.importe)}
            </Typography>
          ))}
        </Box>
      </Box>

      {detalle ? (
        <DetalleOrdenPagoDocumento detalle={detalle} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {comprobantesTabla?.length ?? 0} comprobante
          {(comprobantesTabla?.length ?? 0) !== 1 ? "s" : ""} incluido
          {(comprobantesTabla?.length ?? 0) !== 1 ? "s" : ""} en la orden.
        </Typography>
      )}

      {medioPago?.observaciones ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ p: 1.5, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Observaciones
            </Typography>
            <Typography variant="body2">{medioPago.observaciones}</Typography>
          </Box>
        </>
      ) : null}
    </Paper>
  );
};

export default VistaOrdenPagoDocumento;
